/* eslint-disable max-lines */
import { useCallback, useState, useEffect, createContext, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'

import styled from 'styled-components'
import Tooltip from 'components/Tooltip'
import { color } from 'styles/colors'

import { useSessionContext } from 'hooks/SessionContext'
import LockIcon from '@mui/icons-material/LockOutlined'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import Api from 'services/api'

import { screenSize } from 'styles/shared'

const PremiumFeaturesContext = createContext()

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.shape()
  ]).isRequired
}

export const PremiumFeaturesContextProvider = ({ children }) => {
  const { session, isSessionLoaded } = useSessionContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [activePremiumFeatures, setActivePremiumFeatures] = useState([])
  const [publishedPremiumFeatures, setPublishedPremiumFeatures] = useState([])
  const [tier, setTier] = useState(null)
  const [premiumFreeTrial, setPremiumFreeTrial] = useState(false)
  const [premiumFreeTrialEndDate, setPremiumFreeTrialEndDate] = useState(null)
  const [arePremiumFeaturesLoaded, setPremiumFeaturesLoaded] = useState(false)
  const [featureInfo] = useState([])

  useEffect(() => {
    if (!isSessionLoaded) {
      return
    }
    Api.Paywall.premiumFeatures(session.dealerId)
      .then((res) => {
        setActivePremiumFeatures(res.featureCodes)
        setPublishedPremiumFeatures(res.publishedFeatures)
        setTier(res.tier)
        setPremiumFreeTrial(res.inTrial)
        setPremiumFreeTrialEndDate(res.trialExpiration)
        setPremiumFeaturesLoaded(true)

        res.publishedFeatures.forEach((featureCode) => {
          Api.Paywall.getFeatureInfo(featureCode)
            .then((res) => {
              featureInfo.push({ featureCode, endDate: res.trialEndDate, upgradeMsg: res.upgradeMsg })
            })
        })
      })
      .catch(() => {
        setActivePremiumFeatures([])
        setPublishedPremiumFeatures([])
        setTier(null)
        setPremiumFeaturesLoaded(true)
      })
  }, [location.pathname, location.search, session, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasAccess = useCallback((featureCode) => {
    if (arePremiumFeaturesLoaded === false || activePremiumFeatures.length === 0) {
      return false
    }

    if (activePremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, activePremiumFeatures])

  const showUpgrade = useCallback((featureCode) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded || tier !== 'standard' || inFreeTrial(featureCode)) {
      return false
    }

    if (tier === 'standard' && publishedPremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures, tier]) // eslint-disable-line react-hooks/exhaustive-deps

  const inFreeTrial = useCallback((featureCode) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded || tier !== 'standard') {
      return false
    }

    if (publishedPremiumFeatures.includes(featureCode) && activePremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures, activePremiumFeatures]) // eslint-disable-line react-hooks/exhaustive-deps

  const showFeature = useCallback((featureCode) => {
    if (publishedPremiumFeatures.includes(featureCode) && arePremiumFeaturesLoaded) {
      if (inFreeTrial(featureCode) || showUpgrade(featureCode) || hasAccess(featureCode)) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures]) // eslint-disable-line react-hooks/exhaustive-deps

  const hoverTextMessage = useCallback((featureCode, defaultMessage) => {
    const feature = featureInfo.find((feature) => feature.featureCode === featureCode)
    const upgradeMessage = feature?.upgradeMsg || ''

    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded) {
      return ''
    } else if (showUpgrade(featureCode)) {
      return upgradeMessage
    } else if (inFreeTrial(featureCode)) {
      if (feature?.endDate) {
        return `Available during Diagnostics Premium Free-Trial until ${new Date(feature.endDate).toDateString()}. ${upgradeMessage}`.trim()
      } else {
        return `Available during Diagnostics Premium Free-Trial. ${upgradeMessage}`.trim()
      }
    } else {
      return defaultMessage
    }
  }, [inFreeTrial, showUpgrade]) // eslint-disable-line react-hooks/exhaustive-deps

  const LockIcon = ({
    featureCode,
    defaultHover = '',
    featureComponentType = 'card',
    canShowTooltip = true
  }) => {
    if (
      session.isFsr ||
      session.isAdmin ||
      tier !== 'standard' ||
      !arePremiumFeaturesLoaded
    ) {
      return <></>
    }
    if (showFeature(featureCode) && showUpgrade(featureCode)) {
      return canShowTooltip ? (
        <Tooltip
          title={hoverTextMessage(featureCode, defaultHover)}
        >
          <S.BackgroundCircle feature_component_type={featureComponentType}>
            <S.LockIcon />
          </S.BackgroundCircle>
        </Tooltip>
      ) : (
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.LockIcon />
        </S.BackgroundCircle>
      )
    } else if (showFeature(featureCode)) {
      return canShowTooltip ? (
        <Tooltip
          title={hoverTextMessage(featureCode, defaultHover)}
        >
          <S.BackgroundCircle feature_component_type={featureComponentType}>
            <S.UnlockIcon />
          </S.BackgroundCircle>
        </Tooltip>
      ) : (
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.UnlockIcon />
        </S.BackgroundCircle>
      )
    } else {
      return <></>
    }
  }

  LockIcon.propTypes = {
    featureCode: PropTypes.string.isRequired,
    defaultHover: PropTypes.string,
    featureComponentType: PropTypes.string,
    canShowTooltip: PropTypes.bool
  }

  LockIcon.defaultProps = {
    defaultHover: '',
    featureComponentType: 'card',
    canShowTooltip: true
  }

  const InfoIcon = ({ featureCode }) => {
    if (
      session.isFsr ||
      session.isAdmin ||
      !arePremiumFeaturesLoaded
    ) {
      return <></>
    }
    if (showFeature(featureCode) || showUpgrade(featureCode)) {
      const feature = featureInfo.find((feature) => feature.featureCode === featureCode)

      return (
        <Tooltip
          title={
            <span data-testid="premium-features-info-tooltip">
              {feature.upgradeMsg}
            </span>
          }
        >
          <S.Icon
            src="/img/alert-rth-spirte-icon/device_status_info.svg"
            alt="device status icon"
            tabIndex={0}
            data-testid="premium-features-info-icon"
          />
        </Tooltip>
      )
    } else {
      return <></>
    }
  }

  InfoIcon.propTypes = {
    featureCode: PropTypes.string.isRequired
  }

  return (
    <PremiumFeaturesContext.Provider value={{
      hasAccess,
      showUpgrade,
      inFreeTrial,
      hoverTextMessage,
      LockIcon,
      tier,
      showFeature,
      arePremiumFeaturesLoaded,
      InfoIcon,
      premiumFreeTrial,
      premiumFreeTrialEndDate
    }}
    >
      {children}
    </PremiumFeaturesContext.Provider>
  )
}

PremiumFeaturesContextProvider.propTypes = propTypes

export const usePremiumFeaturesContext = () => useContext(PremiumFeaturesContext)

const iconStyle = `
  font-size: 18px;
`
const backgroundStyleCorner = `
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${color('border')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const backgroundStyleInline = `
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const S = {
  LockIcon: styled(LockIcon)`
    color: ${color('premiumLockedIcon')};
    ${iconStyle}
  `,
  UnlockIcon: styled(UnlockIcon)`
    color: ${color('premiumUnlockedIcon')};
    ${iconStyle}
  `,
  /* eslint-disable camelcase */
  BackgroundCircle: styled.div`
    position: ${({ feature_component_type }) => feature_component_type !== 'title' ? 'absolute' : 'relative'};
    top: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : feature_component_type === 'title' ? '0px' : '-10px'};
    right: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : '-10px'};
    ${({ feature_component_type }) => feature_component_type !== 'title' ? backgroundStyleCorner : backgroundStyleInline};
  `,
  /* eslint-enable camelcase */
  Icon: styled.img`
    width: 27px;
    height: 27px;
    margin-left: 10px;

    @media ${screenSize.small} {
      width: 19px;
      height: 19px;
      margin-left: 5px;
    }
  `
}
